import React, { useEffect, useState } from 'react';
import { MdCheck, MdEdit } from 'react-icons/md';
import { signOut } from "firebase/auth";
import { useLocation, useNavigate, Link, useParams } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db, auth } from "../firebase";
import CodeMirror from '@uiw/react-codemirror';
import { javascript } from '@codemirror/lang-javascript';
import SplitPane from 'react-split-pane';
import logo from '../Image/logo.webp';
import { FaChevronDown, FaCss3, FaHtml5 } from 'react-icons/fa6';
import { FcSettings } from 'react-icons/fc';
import { FaJs } from 'react-icons/fa';
import { AnimatePresence, motion } from 'framer-motion';
import Alert from './Alert';

function NewProject({ setIsLoggedIn, setUser, user }) {
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [html, setHtml] = useState("");
  const [css, setCss] = useState("");
  const [js, setJs] = useState("");
  const [output, setOutput] = useState("");
  const [title, setTitle] = useState("Untitled");
  const [isTitle, setIsTitle] = useState("");
  const [projects, setProjects] = useState([]);
  const [alert, setAlert] = useState(false);
  const [isOwner, setIsOwner] = useState(false);

  useEffect(() => {
    if (location.state && location.state.project) {
      const project = location.state.project;
      setHtml(project.html);
      setCss(project.css);
      setJs(project.js);
      setTitle(project.title);
      setIsOwner(project.user.uid === user.uid);
      updateOutput(project.html, project.css, project.js);
    }
  }, [location.state]);

  useEffect(() => {
    const fetchProjects = async () => {
      const projectsCollection = collection(db, 'Projects');
      const projectsSnapshot = await getDocs(projectsCollection);
      const projectsData = projectsSnapshot.docs.map(doc => doc.data());
      setProjects(projectsData);
    };

    fetchProjects();
  }, []);

  const updateOutput = (htmlContent, cssContent, jsContent) => {
    const combinedOutput = `
      <html>
        <head>
          <style>${cssContent}</style>
        </head>
        <body>
          ${htmlContent}
          <script>${jsContent}</script>
        </body>
      </html>
    `;
    setOutput(combinedOutput);
  }

  useEffect(() => {
    updateOutput(html, css, js);
  }, [html, css, js]);

  const handleLogout = () => {
    signOut(auth)
      .then(() => {
        setUser(null);
        setIsLoggedIn(false);
        navigate("/home/signup");
        console.log("Signed out successfully");
      })
      .catch((error) => {
        // Handle error
      });
  };

  const handleUpdate = async () => {
    const projectRef = doc(db, 'Projects', projectId);
    await updateDoc(projectRef, {
      html: html,
      css: css,
      js: js,
      title: title,
      output:output,
    });
    setAlert(true);

    setTimeout(() => {
      setAlert(false);
    }, 2000);
  };

  return (
    <div className="w-screen h-screen flex flex-col items-start justify-start overflow-hidden">
      {/* Alert section */}
      <AnimatePresence>
        {alert && <Alert status={"Update"} alertmsg={"Changes Saved!"} />}
      </AnimatePresence>
      {/* Header section */}
      <header className='w-full flex items-center justify-between px-12 py-4'>
        <div className="flex items-start justify-center gap-6">
          <Link to={'/home/projects'}>
            <img src={logo} alt="Logo" className="object-contain w-32 h-auto mt-3" />
          </Link>
          <div className="flex flex-col items-start justify-start">
            <div className="flex  items-center justify-center gap-3">
              <AnimatePresence>
                {isTitle ? (
                  <motion.input key={"titleinput"}
                    type="text"
                    placeholder="Your Title"
                    className='px-3 py-2 rounded-md bg-transparent text-primaryText text-base
                        outline-none border-none'
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                ) : (
                  <motion.p key={"titlelabel"} className='px-3 py-2 text-white text-lg'>
                    {title}
                  </motion.p>
                )}
              </AnimatePresence>
              <AnimatePresence>
                {isTitle ? (
                  <motion.div key={"MdCheck"}
                    whileTap={{ scale: 0.9 }}
                    className='cursor-pointer'
                    onClick={() => setIsTitle(false)}
                  >
                    <MdCheck className='text-2xl text-emerald-500' />
                  </motion.div>
                ) : (
                  <motion.div key={"MdEdit"}
                    whileTap={{ scale: 0.9 }}
                    className='cursor-pointer'
                    onClick={() => setIsTitle(true)}
                  >
                    <MdEdit className='text-2xl text-primaryText' />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <div className="flex items-center justify-center px-3 mt-2 gap-2">
              {user && (
                <p className="text-primaryText text-sm">{user.displayName}</p>
              )}
              <p className='text-[10px] bg-emerald-500 rounded-sm px-2 py-[1px] text-primary font-semibold cursor-pointer'>+ Follow</p>
            </div>
          </div>
        </div>
        {user && (
          <div className="flex items-center justify-center gap-4">
            {isOwner && (
              <button className='px-5 py-2 bg-primaryText cursor-pointer text-base text-primary font-semibold rounded-md' onClick={handleUpdate}>Update</button>
            )}
            <Link to={'/home/userwork'} className='flex items-center justify-center gap-6 text-gray-400'>
              <p className='w-14 bg-emerald-400 py-2 px-1 text-center text-white rounded-xl text-2xl hover:bg-emerald-700 cursor-pointer capitalize'>
                {user.email[0]}</p>
            </Link>
            <div className='flex items-center justify-center gap-6 text-gray-400' onClick={handleLogout}>
              <p className='w-24 bg-secondary py-3 px-4 text-center text-white rounded-md text-lg hover:bg-emerald-700 cursor-pointer'>Logout</p>
            </div>
          </div>
        )}
      </header>
      <div>
        <SplitPane split="horizontal" minSize={100} maxSize={-100} defaultSize={"50%"}>
          <SplitPane split='vertical' minSize={500}>
            {/* HTML CODE */}
            <div className="w-full h-full flex flex-col items-start justify-start">
              <div className="w-full flex items-center justify-between">
                <div className="bg-secondary px-4 py-2 border-t-4 flex items-center justify-center gap-3 border-t-gray-500">
                  <FaHtml5 className='text-xl text-sky-500' />
                  <p className='text-primaryText font-semibold '>HTML</p>
                </div>
                <div className="cursor-pointer flex items-center justify-center px-4 gap-5">
                  <FcSettings className='text-xl' />
                  <FaChevronDown className='text-xl text-primaryText' />
                </div>
              </div>
              <div className="bg-primary w-full h-full rounded-md overflow-hidden" style={{ overflow: "Hidden", height: "100%" }}>
                <CodeMirror style={{ border: "none", width: "100%", height: "100%", overflow:"auto" }}
                  value={html}
                  
                  extensions={[javascript({ jsx: true })]}
                  theme={"dark"}
                  onChange={(value, viewUpdate) => {
                    setHtml(value);
                  }}
                />
              </div>
            </div>
            <SplitPane split='vertical' minSize={500}>
              {/* CSS CODE */}
              <div className="w-full h-full flex flex-col items-start justify-start">
                <div className="w-full flex items-center justify-between">
                  <div className="bg-secondary px-4 py-2 border-t-4 flex items-center justify-center gap-3 border-t-gray-500">
                    <FaCss3 className='text-xl text-sky-500' />
                    <p className='text-primaryText font-semibold '>CSS</p>
                  </div>
                  <div className="cursor-pointer flex items-center justify-center px-4 gap-5">
                    <FcSettings className='text-xl' />
                    <FaChevronDown className='text-xl text-primaryText' />
                  </div>
                </div>
                <div className="bg-primary w-full h-full rounded-md overflow-hidden" style={{ overflow: "Hidden", height: "100%" }}>
                  <CodeMirror style={{ border: "none", width: "100%", height: "100%", overflow:"auto" }}
                    value={css}
                   
                    extensions={[javascript({ jsx: true })]}
                    theme={"dark"}
                    onChange={(value, viewUpdate) => {
                      setCss(value);
                    }}
                  />
                </div>
              </div>
              {/* JS CODE */}
              <div className="w-full h-full flex flex-col items-start justify-start">
                <div className="w-full flex items-center justify-between">
                  <div className="bg-secondary px-4 py-2 border-t-4 flex items-center justify-center gap-3 border-t-gray-500">
                    <FaJs className='text-xl text-yellow-500' />
                    <p className='text-primaryText font-semibold '>JS</p>
                  </div>
                  <div className="cursor-pointer flex items-center justify-center px-4 gap-5">
                    <FcSettings className='text-xl' />
                    <FaChevronDown className='text-xl text-primaryText' />
                  </div>
                </div>
                <div className="bg-primary w-full h-full rounded-md overflow-hidden" style={{ overflow: "Hidden", height: "100%" }}>
                  <CodeMirror style={{ border: "none", width: "100%", height: "100%", overflow:"auto" }}
                    value={js}
                    
                    extensions={[javascript({ jsx: true })]}
                    theme={"dark"}
                    onChange={(value, viewUpdate) => {
                      setJs(value);
                    }}
                  />
                </div>
              </div>
            </SplitPane>
          </SplitPane>
          {/* Bottom result */}
          <div className="bg-white" style={{ overflow: "hidden", height: "100%" }}>
            <iframe
              title='Result'
              srcDoc={output}
              style={{ border: "none", width: "100%", height: "100%" }}
              onLoad={() => console.log("Iframe loaded")}
            />
          </div>
        </SplitPane>
      </div>
    </div>
  )
}

export default NewProject;
